'use client'

import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Image from 'next/image'
import { useUTMPersistence } from '@/hooks/useUTMPersistence'
import { trackMetaConversion } from '@/components/MetaPixel'

// Define types for our form data and steps
interface FormData {
  // Contact information
  firstName: string
  lastName: string
  email: string
  phone: string

  // Location information
  plz: string
  ort: string
  strasse: string
  bundesland: string

  // Selection tracking
  selectedServices: string[]
  currentPath: string
  currentServiceIndex: number

  // Photovoltaik fields
  gebaeudeart: string
  gebaeudetype: string
  isEigentuemer: string
  dachform: string
  dachalter: string
  selbstbewohnt: string
  projektstart: string

  // Isolierung fields
  fassadematerial: string
  fassadeGroesse: string
  raumanzahl: string
  innenwandGroesse: string

  // Heizung fields
  heizflaeche: string
  heizraumanzahl: string

  // System fields
  currentStep: number
  path: 'main' | 'alternative' | 'heating' | 'multiselect'
  completedSteps: { [key: string]: boolean }

  // Added fields from previous code
  stromverbrauch?: string
  installiert?: string
  dachtyp?: string
  speicher?: string
  isolationsbereich?: string
  isolationsflaeche?: string
  zeitrahmen?: string
  heizungstyp: string
  baujahr: string
  location: string
  hersteller: string
  heizleistung: string
  stromTarif: string
  wohnflaeche: string
  personenAnzahl: string
  // in your FormData interface
  // source: {
  //   type: 'direct' | 'referral' | 'partnerprogram'
  //   referrerName?: string
  //   referrerEmail?: string
  //   // new partner metadata
  //   partnerCompany?: string
  //   partnerTaxId?: string
  //   partnerIban?: string
  //   // …etc
  // }
  source?: {
    type: 'direct' | 'referral' | 'partnerprogram' | 'hauseigentumer'
    referrerName?: string
    referrerEmail?: string
    partnerCompany?: string
    partnerTaxId?: string
    partnerIban?: string
  }
}

const MultiStepForm = ({
  compact = false,
  className = '',
}: {
  compact?: boolean
  className?: string
}) => {
  // UTM persistence hook
  const { persistedParams, getParamsForSubmission } = useUTMPersistence()

  const [formData, setFormData] = useState<FormData>({
    // Contact information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',

    // Location information
    plz: '',
    ort: '',
    strasse: '',
    bundesland: '',

    // Selection tracking
    selectedServices: [],
    currentPath: '',
    currentServiceIndex: 0,

    // Photovoltaik fields
    gebaeudeart: '',
    gebaeudetype: '',
    isEigentuemer: '',
    dachform: '',
    dachalter: '',
    selbstbewohnt: '',
    projektstart: '',

    // Isolierung fields
    fassadematerial: '',
    fassadeGroesse: '',
    raumanzahl: '',
    innenwandGroesse: '',

    // Heizung fields
    heizflaeche: '',
    heizraumanzahl: '',

    // System fields
    currentStep: 1,
    path: 'main',
    completedSteps: {},

    // Legacy fields
    heizungstyp: '',
    baujahr: '',
    location: '',
    hersteller: '',
    heizleistung: '',
    stromTarif: '',
    wohnflaeche: '',
    personenAnzahl: '',
    // source: {
    //   type: 'direct',
    //   referrerName: '',
    //   referrerEmail: '',
    // },
    source: { type: 'direct' },
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({})
  const [isClient, setIsClient] = useState(false)

  useEffect(() => {
    setIsClient(true)
  }, [])

  // 3. Updated useEffect to handle both legacy URL parameters and new UTM persistence
  useEffect(() => {
    if (!isClient || typeof window === 'undefined') return

    const params = new URLSearchParams(window.location.search)
    const ref = params.get('ref')
    const name = params.get('name')
    const email = params.get('email')
    const company = params.get('company')

    if (ref === 'hero' && name && email) {
      setFormData((prev) => ({
        ...prev,
        source: {
          type: 'referral',
          referrerName: decodeURIComponent(name),
          referrerEmail: decodeURIComponent(email),
        },
      }))
    } else if (ref === 'partnerprogram') {
      setFormData((prev) => ({
        ...prev,
        source: {
          type: 'partnerprogram',
          partnerCompany: decodeURIComponent(company || ''),
        },
      }))
    } else if (ref === 'value-video') {
      // HauseigentumerPage → Termin CTA
      setFormData((prev) => ({
        ...prev,
        source: { type: 'hauseigentumer' },
      }))
    }
  }, [isClient])

  // Get service paths in sequence based on user selection
  const getServicePaths = (): string[] => {
    const paths: string[] = []
    if (formData.selectedServices.includes('Photovoltaik')) paths.push('photovoltaik')
    if (formData.selectedServices.includes('Isolierung')) paths.push('isolierung')
    if (formData.selectedServices.includes('Heizung')) paths.push('heizung')
    return paths
  }

  // Get total steps count for current path
  const getServiceStepCount = (servicePath: string) => {
    switch (servicePath) {
      case 'photovoltaik':
        return 7 // 7 photovoltaik specific questions
      case 'isolierung':
        return 5 // 5 isolierung specific questions
      case 'heizung':
        return 2 // 2 heizung specific questions
      case 'common':
        return 4 // 4 steps that are common to all paths (bundesland, location, contact, success)
      default:
        return 0
    }
  }

  // Calculate total steps across all selected services
  const calculateTotalSteps = () => {
    // First step (service selection) + all service-specific steps + common steps at the end
    const servicePaths = getServicePaths()
    let totalSteps = 1 // Start with the multi-select step

    // Add steps for each selected service path
    servicePaths.forEach((path) => {
      totalSteps += getServiceStepCount(path)
    })

    // Add common steps (bundesland, address, contact info, confirmation)
    totalSteps += getServiceStepCount('common')

    return totalSteps
  }

  // Calculate current progress considering all paths
  const progress = Math.round((formData.currentStep / calculateTotalSteps()) * 100)

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }))
  }

  const handleOptionSelect = (name: string, value: string) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }))

    // Automatically advance to next step after selection
    nextStep()
  }

  // This function determines which service's questions to show next
  const determineNextPath = () => {
    const servicePaths = getServicePaths()

    // If we've just completed the multi-select step
    if (formData.currentStep === 1) {
      // If no services selected, stay on current step
      if (servicePaths.length === 0) return { path: 'main', serviceIndex: 0 }

      // Start with the first selected service
      return { path: servicePaths[0], serviceIndex: 0 }
    }

    // If we're on a service path, check if we've completed all its questions
    const currentPathIndex = servicePaths.indexOf(formData.currentPath)

    if (currentPathIndex >= 0) {
      const currentPathStepCount = getServiceStepCount(formData.currentPath)
      const isCurrentPathComplete = formData.currentServiceIndex >= currentPathStepCount - 1

      if (isCurrentPathComplete) {
        // Move to the next service path, if any
        if (currentPathIndex < servicePaths.length - 1) {
          return {
            path: servicePaths[currentPathIndex + 1],
            serviceIndex: 0,
          }
        } else {
          // All service paths completed, move to common questions
          return { path: 'common', serviceIndex: 0 }
        }
      } else {
        // Continue with current service path
        return {
          path: formData.currentPath,
          serviceIndex: formData.currentServiceIndex + 1,
        }
      }
    }

    // If we're in the common section, just increase the step index
    if (formData.currentPath === 'common') {
      return {
        path: 'common',
        serviceIndex: formData.currentServiceIndex + 1,
      }
    }

    // Default - start with first selected service
    return { path: servicePaths[0] || 'main', serviceIndex: 0 }
  }

  // This function validates the current step's required fields
  const validateCurrentStep = (): boolean => {
    const currentErrors: { [key: string]: string } = {}
    let isValid = true

    // Common steps validation
    if (formData.currentPath === 'common') {
      if (formData.currentServiceIndex === 1) {
        // Location step
        if (!formData.plz) {
          currentErrors.plz = 'PLZ ist erforderlich'
          isValid = false
        }
        if (!formData.ort) {
          currentErrors.ort = 'Ort ist erforderlich'
          isValid = false
        }
        if (!formData.strasse) {
          currentErrors.strasse = 'Straße ist erforderlich'
          isValid = false
        }
      } else if (formData.currentServiceIndex === 2) {
        // Contact info step
        if (!formData.firstName) {
          currentErrors.firstName = 'Vorname ist erforderlich'
          isValid = false
        }
        if (!formData.lastName) {
          currentErrors.lastName = 'Nachname ist erforderlich'
          isValid = false
        }
        if (!formData.email) {
          currentErrors.email = 'Email ist erforderlich'
          isValid = false
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
          currentErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein'
          isValid = false
        }
        if (!formData.phone) {
          currentErrors.phone = 'Telefon ist erforderlich'
          isValid = false
        }
      }
    }

    // Isolierung path validation
    if (formData.currentPath === 'isolierung') {
      switch (formData.currentServiceIndex) {
        case 1:
          if (!formData.fassadeGroesse) {
            currentErrors.fassadeGroesse = 'Fläche ist erforderlich'
            isValid = false
          }
          break
        case 2:
          if (!formData.raumanzahl) {
            currentErrors.raumanzahl = 'Raumanzahl ist erforderlich'
            isValid = false
          }
          break
        case 3:
          if (!formData.innenwandGroesse) {
            currentErrors.innenwandGroesse = 'Fläche ist erforderlich'
            isValid = false
          }
          break
      }
    }

    // Heizung path validation
    if (formData.currentPath === 'heizung') {
      switch (formData.currentServiceIndex) {
        case 0:
          if (!formData.heizflaeche) {
            currentErrors.heizflaeche = 'Heizfläche ist erforderlich'
            isValid = false
          }
          break
        case 1:
          if (!formData.heizraumanzahl) {
            currentErrors.heizraumanzahl = 'Raumanzahl ist erforderlich'
            isValid = false
          }
          break
      }
    }

    setFieldErrors(currentErrors)
    return isValid
  }

  // Move to the next step
  const nextStep = () => {
    // Validate the current step first
    if (!validateCurrentStep()) {
      return // Stop if validation fails
    }

    const { path, serviceIndex } = determineNextPath()

    setFormData((prevData) => ({
      ...prevData,
      currentStep: prevData.currentStep + 1,
      currentPath: path,
      currentServiceIndex: serviceIndex,
    }))

    // Clear field errors when moving to next step
    setFieldErrors({})
  }

  // Move to the previous step
  const prevStep = () => {
    if (formData.currentStep > 1) {
      // Get all service paths
      const servicePaths = getServicePaths()

      // Handle back navigation specifically
      if (formData.currentPath === 'common' && formData.currentServiceIndex > 0) {
        // If we're in common questions, just move back in the common steps
        setFormData((prevData) => ({
          ...prevData,
          currentStep: prevData.currentStep - 1,
          currentServiceIndex: prevData.currentServiceIndex - 1,
        }))
      } else if (formData.currentPath === 'common' && formData.currentServiceIndex === 0) {
        // If we're at the first common question, go back to the last question of the last service
        const lastServicePath = servicePaths[servicePaths.length - 1]
        const lastServiceStepCount = getServiceStepCount(lastServicePath)

        setFormData((prevData) => ({
          ...prevData,
          currentStep: prevData.currentStep - 1,
          currentPath: lastServicePath,
          currentServiceIndex: lastServiceStepCount - 1,
        }))
      } else if (formData.currentServiceIndex > 0) {
        // If we're not at the first question of a service, move back in the current service
        setFormData((prevData) => ({
          ...prevData,
          currentStep: prevData.currentStep - 1,
          currentServiceIndex: prevData.currentServiceIndex - 1,
        }))
      } else {
        // We're at the first question of a service, go back to the previous service or multi-select
        const currentPathIndex = servicePaths.indexOf(formData.currentPath)

        if (currentPathIndex > 0) {
          // Go to the last question of the previous service
          const prevServicePath = servicePaths[currentPathIndex - 1]
          const prevServiceStepCount = getServiceStepCount(prevServicePath)

          setFormData((prevData) => ({
            ...prevData,
            currentStep: prevData.currentStep - 1,
            currentPath: prevServicePath,
            currentServiceIndex: prevServiceStepCount - 1,
          }))
        } else {
          // Go back to the multi-select page
          setFormData((prevData) => ({
            ...prevData,
            currentStep: 1,
            currentPath: '',
            currentServiceIndex: 0,
          }))
        }
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateCurrentStep()) return

    setIsSubmitting(true)
    setError(null)

    // Get persisted tracking parameters
    const trackingParams = getParamsForSubmission()

    // SAFELY EXTRACT from URL AGAIN during submission (for legacy support)
    let params: URLSearchParams | null = null
    let ref: string | null = null
    let name = ''
    let email = ''
    let company = ''
    let taxId = ''
    let iban = ''

    if (typeof window !== 'undefined') {
      params = new URLSearchParams(window.location.search)
      ref = params.get('ref')
      name = decodeURIComponent(params.get('name') || '')
      email = decodeURIComponent(params.get('email') || '')
      company = decodeURIComponent(params.get('company') || '')
      taxId = decodeURIComponent(params.get('taxId') || '')
      iban = decodeURIComponent(params.get('iban') || '')
    }

    const effectiveRef = ref || (trackingParams as any)?.ref || null

    const source =
      effectiveRef === 'partnerprogram'
        ? {
            type: 'partnerprogram' as const,
            referrerName: name,
            referrerEmail: email,
            partnerCompany: company,
            partnerTaxId: taxId,
            partnerIban: iban,
          }
        : ref === 'hero' && name && email
          ? {
              type: 'referral' as const,
              referrerName: name,
              referrerEmail: email,
            }
          : effectiveRef === 'value-video'
            ? {
                type: 'hauseigentumer' as const, // HauseigentumerPage flow
              }
            : (formData.source ?? { type: 'direct' as const }) // fallback to anything set earlier

    try {
      const response = await fetch(
        'https://services.leadconnectorhq.com/hooks/7Zb5aUMSIgLtL6NPemOS/webhook-trigger/c2146f40-4cbc-46e6-8f77-7f06d18dd71f',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...formData,
            // Legacy tracking (for backward compatibility)
            leadSource: source.type,
            referrerName: 'referrerName' in source ? source.referrerName : undefined,
            referrerEmail: 'referrerEmail' in source ? source.referrerEmail : undefined,
            partnerCompany: 'partnerCompany' in source ? source.partnerCompany : undefined,
            partnerTaxId: 'partnerTaxId' in source ? source.partnerTaxId : undefined,
            partnerIban: 'partnerIban' in source ? source.partnerIban : undefined,
            source: source,
            // New UTM tracking parameters
            trackingParams: trackingParams,
            utm_source: trackingParams.utm_source,
            utm_medium: trackingParams.utm_medium,
            utm_campaign: trackingParams.utm_campaign,
            utm_term: trackingParams.utm_term,
            utm_content: trackingParams.utm_content,
            utm_id: trackingParams.utm_id,
            landingPage: trackingParams.landingPage,
            trackingTimestamp: trackingParams.timestamp,
            // Meta specific tracking
            fbclid: trackingParams.fbclid,
            metaSource: trackingParams.fbclid ? 'meta_ads' : null,
            adPlatform: trackingParams.fbclid ? 'facebook' : trackingParams.gclid ? 'google' : null,
          }),
        }
      )

      if (response.ok) {
        // Track Meta conversion with both form data and UTM parameters
        trackMetaConversion(formData, trackingParams)

        setSubmitted(true)
        setFormData((prevData) => ({
          ...prevData,
          currentServiceIndex: 3,
          currentStep: calculateTotalSteps(),
        }))
      } else {
        throw new Error('Failed to submit form')
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      setError('Es gab ein Problem beim Absenden des Formulars. Bitte versuchen Sie es erneut.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // This function handles toggling a service in the multi-select
  const toggleServiceSelection = (service: string) => {
    setFormData((prevData) => {
      const currentSelections = [...prevData.selectedServices]

      if (currentSelections.includes(service)) {
        // Remove if already selected
        return {
          ...prevData,
          selectedServices: currentSelections.filter((item) => item !== service),
        }
      } else {
        // Add if not selected
        return {
          ...prevData,
          selectedServices: [...currentSelections, service],
        }
      }
    })
  }

  // Define step content based on current step and path
  const renderStepContent = () => {
    // Step 1: Multi-select service options
    if (formData.currentStep === 1) {
      return (
        <StepContainer title="Für welche Energielösungen interessieren Sie sich?">
          <p className="mb-6 text-center text-lg text-gray-600">
            Wählen Sie eine oder mehrere Optionen aus
          </p>
          <div className="grid flex-grow grid-cols-1 gap-6 py-6 md:grid-cols-3">
            <MultiSelectCard
              title="Volks-Photovoltaik"
              description="Solarenergie für Ihr Zuhause"
              icon="/images/solar-panel.webp"
              selected={formData.selectedServices.includes('Photovoltaik')}
              onClick={() => toggleServiceSelection('Photovoltaik')}
            />
            <MultiSelectCard
              title="Volks-Isolierung"
              description="Energieeffizienz für Ihr Zuhause"
              icon="/images/Aufsichtig_InsideCoating_Front_-1024x760.png"
              selected={formData.selectedServices.includes('Isolierung')}
              onClick={() => toggleServiceSelection('Isolierung')}
            />
            <MultiSelectCard
              title="Volks-Heizung"
              description="Wärmesysteme für Ihr Zuhause"
              icon="/images/heating-system.webp"
              selected={formData.selectedServices.includes('Heizung')}
              onClick={() => toggleServiceSelection('Heizung')}
            />
          </div>
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={nextStep}
              disabled={formData.selectedServices.length === 0}
              className={`rounded bg-[#ff4500] px-6 py-2 text-white transition-colors ${
                formData.selectedServices.length === 0
                  ? 'cursor-not-allowed opacity-50'
                  : 'hover:bg-[#ff6a33]'
              }`}
            >
              Weiter
            </button>
          </div>
        </StepContainer>
      )
    }

    // Photovoltaik questions
    if (formData.currentPath === 'photovoltaik') {
      switch (formData.currentServiceIndex) {
        case 0:
          return (
            <StepContainer title="Wo soll die Photovoltaikanlage installiert werden?">
              <OptionGrid>
                <OptionCard
                  title="Einfamilienhaus"
                  onClick={() => handleOptionSelect('gebaeudeart', 'Einfamilienhaus')}
                  selected={formData.gebaeudeart === 'Einfamilienhaus'}
                />
                <OptionCard
                  title="Zweifamilienhaus"
                  onClick={() => handleOptionSelect('gebaeudeart', 'Zweifamilienhaus')}
                  selected={formData.gebaeudeart === 'Zweifamilienhaus'}
                />
                <OptionCard
                  title="Mehrfamilienhaus / Wohnanlage"
                  onClick={() => handleOptionSelect('gebaeudeart', 'Mehrfamilienhaus')}
                  selected={formData.gebaeudeart === 'Mehrfamilienhaus'}
                />
                <OptionCard
                  title="Bürogebäude / Geschäftsgebäude"
                  onClick={() => handleOptionSelect('gebaeudeart', 'Bürogebäude')}
                  selected={formData.gebaeudeart === 'Bürogebäude'}
                />
                <OptionCard
                  title="Freilandflaeche"
                  onClick={() => handleOptionSelect('gebaeudeart', 'Freilandflaeche')}
                  selected={formData.gebaeudeart === 'Freilandflaeche'}
                />
                <OptionCard
                  title="Sonstiges"
                  onClick={() => handleOptionSelect('gebaeudeart', 'Sonstiges')}
                  selected={formData.gebaeudeart === 'Sonstiges'}
                />
              </OptionGrid>
            </StepContainer>
          )
        case 1:
          return (
            <StepContainer title="Um welchen Gebäudetyp handelt es sich?">
              <OptionGrid>
                <OptionCard
                  title="Freistehendes Haus"
                  onClick={() => handleOptionSelect('gebaeudetype', 'Freistehendes Haus')}
                  selected={formData.gebaeudetype === 'Freistehendes Haus'}
                />
                <OptionCard
                  title="Doppelhaushälfte"
                  onClick={() => handleOptionSelect('gebaeudetype', 'Doppelhaushälfte')}
                  selected={formData.gebaeudetype === 'Doppelhaushälfte'}
                />
                <OptionCard
                  title="Reihenmittelhaus"
                  onClick={() => handleOptionSelect('gebaeudetype', 'Reihenmittelhaus')}
                  selected={formData.gebaeudetype === 'Reihenmittelhaus'}
                />
                <OptionCard
                  title="Reihenendhaus"
                  onClick={() => handleOptionSelect('gebaeudetype', 'Reihenendhaus')}
                  selected={formData.gebaeudetype === 'Reihenendhaus'}
                />
              </OptionGrid>
            </StepContainer>
          )
        case 2:
          return (
            <StepContainer title="Sind Sie Eigentümer der Immobilie?">
              <OptionGrid>
                <OptionCard
                  title="Eigner / Entscheidungsbefugt"
                  onClick={() => handleOptionSelect('isEigentuemer', 'Ja')}
                  selected={formData.isEigentuemer === 'Ja'}
                />
                <OptionCard
                  title="Nein"
                  onClick={() => handleOptionSelect('isEigentuemer', 'Nein')}
                  selected={formData.isEigentuemer === 'Nein'}
                />
              </OptionGrid>
            </StepContainer>
          )
        case 3:
          return (
            <StepContainer title="Um welche Dachform handelt es sich?">
              <OptionGrid>
                <OptionCard
                  title="Satteldach"
                  onClick={() => handleOptionSelect('dachform', 'Satteldach')}
                  selected={formData.dachform === 'Satteldach'}
                />
                <OptionCard
                  title="Pultdach"
                  onClick={() => handleOptionSelect('dachform', 'Pultdach')}
                  selected={formData.dachform === 'Pultdach'}
                />
                <OptionCard
                  title="Flachdach"
                  onClick={() => handleOptionSelect('dachform', 'Flachdach')}
                  selected={formData.dachform === 'Flachdach'}
                />
                <OptionCard
                  title="Walmdach"
                  onClick={() => handleOptionSelect('dachform', 'Walmdach')}
                  selected={formData.dachform === 'Walmdach'}
                />
                <OptionCard
                  title="Sonstiges"
                  onClick={() => handleOptionSelect('dachform', 'Sonstiges')}
                  selected={formData.dachform === 'Sonstiges'}
                />
              </OptionGrid>
            </StepContainer>
          )
        case 4:
          return (
            <StepContainer title="Wie alt ist Ihr Dach?">
              <OptionGrid>
                <OptionCard
                  title="Unter 30 Jahre"
                  onClick={() => handleOptionSelect('dachalter', 'Unter 30 Jahre')}
                  selected={formData.dachalter === 'Unter 30 Jahre'}
                />
                <OptionCard
                  title="30 - 70 Jahre"
                  onClick={() => handleOptionSelect('dachalter', '30 - 70 Jahre')}
                  selected={formData.dachalter === '30 - 70 Jahre'}
                />
                <OptionCard
                  title="Über 70 Jahre"
                  onClick={() => handleOptionSelect('dachalter', 'Über 70 Jahre')}
                  selected={formData.dachalter === 'Über 70 Jahre'}
                />
              </OptionGrid>
            </StepContainer>
          )
        case 5:
          return (
            <StepContainer title="Bewohnen Sie die Immobilie selbst?">
              <OptionGrid>
                <OptionCard
                  title="Ja"
                  onClick={() => handleOptionSelect('selbstbewohnt', 'Ja')}
                  selected={formData.selbstbewohnt === 'Ja'}
                />
                <OptionCard
                  title="Nein"
                  onClick={() => handleOptionSelect('selbstbewohnt', 'Nein')}
                  selected={formData.selbstbewohnt === 'Nein'}
                />
              </OptionGrid>
            </StepContainer>
          )
        case 6:
          return (
            <StepContainer title="Wann möchten Sie mit Ihrem Projekt starten?">
              <OptionGrid>
                <OptionCard
                  title="Umgehend"
                  onClick={() => handleOptionSelect('projektstart', 'Umgehend')}
                  selected={formData.projektstart === 'Umgehend'}
                />
                <OptionCard
                  title="In den nächsten 3 Monaten"
                  onClick={() => handleOptionSelect('projektstart', 'In den nächsten 3 Monaten')}
                  selected={formData.projektstart === 'In den nächsten 3 Monaten'}
                />
                <OptionCard
                  title="In 3 bis 6 Monaten"
                  onClick={() => handleOptionSelect('projektstart', 'In 3 bis 6 Monaten')}
                  selected={formData.projektstart === 'In 3 bis 6 Monaten'}
                />
              </OptionGrid>
            </StepContainer>
          )
        default:
          return null
      }
    }

    // Heating path questions
    if (formData.currentPath === 'heating') {
      switch (formData.currentServiceIndex) {
        case 0:
          return (
            <StepContainer title="Was für eine Heizung haben Sie derzeit?">
              <OptionGrid>
                <OptionCard
                  title="Gasheizung"
                  icon="/images/heating-system.png"
                  onClick={() => handleOptionSelect('heizungstyp', 'Gas')}
                  selected={formData.heizungstyp === 'Gas'}
                />
                <OptionCard
                  title="Ölheizung"
                  icon="/images/heating-system.png"
                  onClick={() => handleOptionSelect('heizungstyp', 'Öl')}
                  selected={formData.heizungstyp === 'Öl'}
                />
                <OptionCard
                  title="Elektroheizung"
                  icon="/images/heating-system.png"
                  onClick={() => handleOptionSelect('heizungstyp', 'Elektro')}
                  selected={formData.heizungstyp === 'Elektro'}
                />
              </OptionGrid>
            </StepContainer>
          )
        case 1:
          return (
            <StepContainer title="Wie alt ist Ihre derzeitige Heizung?">
              <InputField
                label="Baujahr"
                name="baujahr"
                type="text"
                value={formData.baujahr}
                onChange={handleInputChange}
                placeholder="z.B. 2005"
                fieldErrors={fieldErrors}
              />
              <div className="mt-4">
                <button
                  type="button"
                  onClick={nextStep}
                  className="rounded bg-[#ff4500] px-6 py-2 text-white hover:bg-[#ff6a33]"
                >
                  Weiter
                </button>
              </div>
            </StepContainer>
          )
        case 2:
          return (
            <StepContainer title="Wie groß ist Ihre Wohnfläche?">
              <InputField
                label="Wohnfläche in m²"
                name="wohnflaeche"
                type="number"
                value={formData.wohnflaeche}
                onChange={handleInputChange}
                placeholder="z.B. 120"
                fieldErrors={fieldErrors}
              />
              <div className="mt-4">
                <button
                  type="button"
                  onClick={nextStep}
                  className="rounded bg-[#ff4500] px-6 py-2 text-white hover:bg-[#ff6a33]"
                >
                  Weiter
                </button>
              </div>
            </StepContainer>
          )
        case 3:
          return (
            <StepContainer title="Wie viele Personen leben in Ihrem Haushalt?">
              <InputField
                label="Anzahl der Personen"
                name="personenAnzahl"
                type="number"
                value={formData.personenAnzahl}
                onChange={handleInputChange}
                placeholder="z.B. 4"
                fieldErrors={fieldErrors}
              />
              <div className="mt-4">
                <button
                  type="button"
                  onClick={nextStep}
                  className="rounded bg-[#ff4500] px-6 py-2 text-white hover:bg-[#ff6a33]"
                >
                  Weiter
                </button>
              </div>
            </StepContainer>
          )
        default:
          return null
      }
    }

    // Alternative path (Isolierung)
    if (formData.currentPath === 'alternative') {
      switch (formData.currentServiceIndex) {
        case 0:
          return (
            <StepContainer title="Was möchten Sie isolieren lassen?">
              <OptionGrid>
                <OptionCard
                  title="Dach/Obergeschoss"
                  icon="/images/isolierung1.png"
                  onClick={() => handleOptionSelect('isolationsbereich', 'Dach')}
                  selected={formData.isolationsbereich === 'Dach'}
                />
                <OptionCard
                  title="Außenwände"
                  icon="/images/isolierung2.png"
                  onClick={() => handleOptionSelect('isolationsbereich', 'Außenwände')}
                  selected={formData.isolationsbereich === 'Außenwände'}
                />
                <OptionCard
                  title="Keller"
                  icon="/images/isolierung3.png"
                  onClick={() => handleOptionSelect('isolationsbereich', 'Keller')}
                  selected={formData.isolationsbereich === 'Keller'}
                />
              </OptionGrid>
            </StepContainer>
          )
        case 1:
          return (
            <StepContainer title="Wie groß ist die zu isolierende Fläche?">
              <InputField
                label="Fläche in m²"
                name="isolationsflaeche"
                type="number"
                value={formData.isolationsflaeche || ''}
                onChange={handleInputChange}
                placeholder="z.B. 100"
                fieldErrors={fieldErrors}
              />
              <div className="mt-4">
                <button
                  type="button"
                  onClick={nextStep}
                  className="rounded bg-[#ff4500] px-6 py-2 text-white hover:bg-[#ff6a33]"
                >
                  Weiter
                </button>
              </div>
            </StepContainer>
          )
        case 2:
          return (
            <StepContainer title="Wann soll die Isolierung durchgeführt werden?">
              <OptionGrid>
                <OptionCard
                  title="So schnell wie möglich"
                  onClick={() => handleOptionSelect('zeitrahmen', 'Schnell')}
                  selected={formData.zeitrahmen === 'Schnell'}
                />
                <OptionCard
                  title="In den nächsten 3 Monaten"
                  onClick={() => handleOptionSelect('zeitrahmen', '3 Monate')}
                  selected={formData.zeitrahmen === '3 Monate'}
                />
                <OptionCard
                  title="In den nächsten 6 Monaten"
                  onClick={() => handleOptionSelect('zeitrahmen', '6 Monate')}
                  selected={formData.zeitrahmen === '6 Monate'}
                />
              </OptionGrid>
            </StepContainer>
          )
        case 3:
          return (
            <StepContainer title="Wann möchten Sie mit der Isolierung beginnen?">
              <OptionGrid>
                <OptionCard
                  title="So schnell wie möglich"
                  onClick={() => handleOptionSelect('zeitrahmen', 'Schnell')}
                  selected={formData.zeitrahmen === 'Schnell'}
                />
                <OptionCard
                  title="In den nächsten 3 Monaten"
                  onClick={() => handleOptionSelect('zeitrahmen', '3 Monate')}
                  selected={formData.zeitrahmen === '3 Monate'}
                />
                <OptionCard
                  title="In den nächsten 6 Monaten"
                  onClick={() => handleOptionSelect('zeitrahmen', '6 Monate')}
                  selected={formData.zeitrahmen === '6 Monate'}
                />
              </OptionGrid>
            </StepContainer>
          )
        default:
          return null
      }
    }

    // Isolierung questions
    if (formData.currentPath === 'isolierung') {
      switch (formData.currentServiceIndex) {
        case 0:
          return (
            <StepContainer title="Welches Material hat Ihre Außenfassade?">
              <OptionGrid>
                <OptionCard
                  title="Putzfassaden"
                  onClick={() => handleOptionSelect('fassadematerial', 'Putzfassaden')}
                  selected={formData.fassadematerial === 'Putzfassaden'}
                />
                <OptionCard
                  title="Klinker & Verblendsteine"
                  onClick={() => handleOptionSelect('fassadematerial', 'Klinker & Verblendsteine')}
                  selected={formData.fassadematerial === 'Klinker & Verblendsteine'}
                />
                <OptionCard
                  title="Naturstein"
                  onClick={() => handleOptionSelect('fassadematerial', 'Naturstein')}
                  selected={formData.fassadematerial === 'Naturstein'}
                />
                <OptionCard
                  title="Holz"
                  onClick={() => handleOptionSelect('fassadematerial', 'Holz')}
                  selected={formData.fassadematerial === 'Holz'}
                />
                <OptionCard
                  title="Metall"
                  onClick={() => handleOptionSelect('fassadematerial', 'Metall')}
                  selected={formData.fassadematerial === 'Metall'}
                />
                <OptionCard
                  title="Keramik & Feinsteinzeug"
                  onClick={() => handleOptionSelect('fassadematerial', 'Keramik & Feinsteinzeug')}
                  selected={formData.fassadematerial === 'Keramik & Feinsteinzeug'}
                />
                <OptionCard
                  title="Faserzement"
                  onClick={() => handleOptionSelect('fassadematerial', 'Faserzement')}
                  selected={formData.fassadematerial === 'Faserzement'}
                />
                <OptionCard
                  title="Ziegel & Mauerwerk"
                  onClick={() => handleOptionSelect('fassadematerial', 'Ziegel & Mauerwerk')}
                  selected={formData.fassadematerial === 'Ziegel & Mauerwerk'}
                />
                <OptionCard
                  title="Kunststoff"
                  onClick={() => handleOptionSelect('fassadematerial', 'Kunststoff')}
                  selected={formData.fassadematerial === 'Kunststoff'}
                />
                <OptionCard
                  title="Sonstiges"
                  onClick={() => handleOptionSelect('fassadematerial', 'Sonstiges')}
                  selected={formData.fassadematerial === 'Sonstiges'}
                />
              </OptionGrid>
            </StepContainer>
          )
        case 1:
          return (
            <StepContainer title="Wie groß ist die Fläche deiner Außenfassade in m²?">
              <InputField
                label="Fläche in m²"
                name="fassadeGroesse"
                type="number"
                value={formData.fassadeGroesse}
                onChange={handleInputChange}
                placeholder="z.B. 120"
                fieldErrors={fieldErrors}
              />
              <div className="mt-4">
                <button
                  type="button"
                  onClick={nextStep}
                  className="rounded bg-[#ff4500] px-6 py-2 text-white hover:bg-[#ff6a33]"
                >
                  Weiter
                </button>
              </div>
            </StepContainer>
          )
        case 2:
          return (
            <StepContainer title="Wie viele Räume hat deine Immobilie?">
              <InputField
                label="Raumanzahl"
                name="raumanzahl"
                type="number"
                value={formData.raumanzahl}
                onChange={handleInputChange}
                placeholder="z.B. 5"
                fieldErrors={fieldErrors}
              />
              <div className="mt-4">
                <button
                  type="button"
                  onClick={nextStep}
                  className="rounded bg-[#ff4500] px-6 py-2 text-white hover:bg-[#ff6a33]"
                >
                  Weiter
                </button>
              </div>
            </StepContainer>
          )
        case 3:
          return (
            <StepContainer title="Wie groß ist die Fläche deiner Innenwände inklusive Decke in m²?">
              <InputField
                label="Fläche in m²"
                name="innenwandGroesse"
                type="number"
                value={formData.innenwandGroesse}
                onChange={handleInputChange}
                placeholder="z.B. 200"
                fieldErrors={fieldErrors}
              />
              <div className="mt-4">
                <button
                  type="button"
                  onClick={nextStep}
                  className="rounded bg-[#ff4500] px-6 py-2 text-white hover:bg-[#ff6a33]"
                >
                  Weiter
                </button>
              </div>
            </StepContainer>
          )
        case 4:
          return (
            <StepContainer title="Wann möchten Sie mit der Isolierung beginnen?">
              <OptionGrid>
                <OptionCard
                  title="So schnell wie möglich"
                  onClick={() => handleOptionSelect('zeitrahmen', 'Schnell')}
                  selected={formData.zeitrahmen === 'Schnell'}
                />
                <OptionCard
                  title="In den nächsten 3 Monaten"
                  onClick={() => handleOptionSelect('zeitrahmen', '3 Monate')}
                  selected={formData.zeitrahmen === '3 Monate'}
                />
                <OptionCard
                  title="In den nächsten 6 Monaten"
                  onClick={() => handleOptionSelect('zeitrahmen', '6 Monate')}
                  selected={formData.zeitrahmen === '6 Monate'}
                />
              </OptionGrid>
            </StepContainer>
          )
        default:
          return null
      }
    }

    // Heizung questions
    if (formData.currentPath === 'heizung') {
      switch (formData.currentServiceIndex) {
        case 0:
          return (
            <StepContainer title="Wie groß ist die Heizfläche in Ihrer Immobilie in m²?">
              <InputField
                label="Heizfläche in m²"
                name="heizflaeche"
                type="number"
                value={formData.heizflaeche}
                onChange={handleInputChange}
                placeholder="z.B. 150"
                fieldErrors={fieldErrors}
              />
              <div className="mt-4">
                <button
                  type="button"
                  onClick={nextStep}
                  className="rounded bg-[#ff4500] px-6 py-2 text-white hover:bg-[#ff6a33]"
                >
                  Weiter
                </button>
              </div>
            </StepContainer>
          )
        case 1:
          return (
            <StepContainer title="Wie viel Räume sollen beheizt werden?">
              <InputField
                label="Raumanzahl"
                name="heizraumanzahl"
                type="number"
                value={formData.heizraumanzahl}
                onChange={handleInputChange}
                placeholder="z.B. 6"
                fieldErrors={fieldErrors}
              />
              <div className="mt-4">
                <button
                  type="button"
                  onClick={nextStep}
                  className="rounded bg-[#ff4500] px-6 py-2 text-white hover:bg-[#ff6a33]"
                >
                  Weiter
                </button>
              </div>
            </StepContainer>
          )
        default:
          return null
      }
    }

    // Common steps for all paths
    if (formData.currentPath === 'common') {
      switch (formData.currentServiceIndex) {
        case 0:
          return (
            <StepContainer title="In welchem Bundesland befindet sich Ihr Objekt?">
              <div className="grid max-h-[350px] grid-cols-2 gap-2 overflow-y-auto pr-1 md:grid-cols-3 lg:grid-cols-4">
                <OptionCard
                  title="Baden-Württemberg"
                  onClick={() => handleOptionSelect('bundesland', 'Baden-Württemberg')}
                  selected={formData.bundesland === 'Baden-Württemberg'}
                  className="p-2 text-sm"
                />
                <OptionCard
                  title="Bayern"
                  onClick={() => handleOptionSelect('bundesland', 'Bayern')}
                  selected={formData.bundesland === 'Bayern'}
                  className="p-2 text-sm"
                />
                <OptionCard
                  title="Berlin"
                  onClick={() => handleOptionSelect('bundesland', 'Berlin')}
                  selected={formData.bundesland === 'Berlin'}
                  className="p-2 text-sm"
                />
                <OptionCard
                  title="Brandenburg"
                  onClick={() => handleOptionSelect('bundesland', 'Brandenburg')}
                  selected={formData.bundesland === 'Brandenburg'}
                  className="p-2 text-sm"
                />
                <OptionCard
                  title="Bremen"
                  onClick={() => handleOptionSelect('bundesland', 'Bremen')}
                  selected={formData.bundesland === 'Bremen'}
                  className="p-2 text-sm"
                />
                <OptionCard
                  title="Hamburg"
                  onClick={() => handleOptionSelect('bundesland', 'Hamburg')}
                  selected={formData.bundesland === 'Hamburg'}
                  className="p-2 text-sm"
                />
                <OptionCard
                  title="Hessen"
                  onClick={() => handleOptionSelect('bundesland', 'Hessen')}
                  selected={formData.bundesland === 'Hessen'}
                  className="p-2 text-sm"
                />
                <OptionCard
                  title="Mecklenburg-Vorpommern"
                  onClick={() => handleOptionSelect('bundesland', 'Mecklenburg-Vorpommern')}
                  selected={formData.bundesland === 'Mecklenburg-Vorpommern'}
                  className="p-2 text-sm"
                />
                <OptionCard
                  title="Niedersachsen"
                  onClick={() => handleOptionSelect('bundesland', 'Niedersachsen')}
                  selected={formData.bundesland === 'Niedersachsen'}
                  className="p-2 text-sm"
                />
                <OptionCard
                  title="Nordrhein-Westfalen"
                  onClick={() => handleOptionSelect('bundesland', 'Nordrhein-Westfalen')}
                  selected={formData.bundesland === 'Nordrhein-Westfalen'}
                  className="p-2 text-sm"
                />
                <OptionCard
                  title="Rheinland-Pfalz"
                  onClick={() => handleOptionSelect('bundesland', 'Rheinland-Pfalz')}
                  selected={formData.bundesland === 'Rheinland-Pfalz'}
                  className="p-2 text-sm"
                />
                <OptionCard
                  title="Saarland"
                  onClick={() => handleOptionSelect('bundesland', 'Saarland')}
                  selected={formData.bundesland === 'Saarland'}
                  className="p-2 text-sm"
                />
                <OptionCard
                  title="Sachsen"
                  onClick={() => handleOptionSelect('bundesland', 'Sachsen')}
                  selected={formData.bundesland === 'Sachsen'}
                  className="p-2 text-sm"
                />
                <OptionCard
                  title="Sachsen-Anhalt"
                  onClick={() => handleOptionSelect('bundesland', 'Sachsen-Anhalt')}
                  selected={formData.bundesland === 'Sachsen-Anhalt'}
                  className="p-2 text-sm"
                />
                <OptionCard
                  title="Schleswig-Holstein"
                  onClick={() => handleOptionSelect('bundesland', 'Schleswig-Holstein')}
                  selected={formData.bundesland === 'Schleswig-Holstein'}
                  className="p-2 text-sm"
                />
                <OptionCard
                  title="Thüringen"
                  onClick={() => handleOptionSelect('bundesland', 'Thüringen')}
                  selected={formData.bundesland === 'Thüringen'}
                  className="p-2 text-sm"
                />
              </div>
            </StepContainer>
          )
        // case 1:
        //   return null;
        case 1:
          return (
            <StepContainer title="Wo soll Ihre Energielösung geplant werden?">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <InputField
                  label="PLZ"
                  name="plz"
                  type="text"
                  value={formData.plz}
                  onChange={handleInputChange}
                  required
                  fieldErrors={fieldErrors}
                />
                <InputField
                  label="Ort"
                  name="ort"
                  type="text"
                  value={formData.ort}
                  onChange={handleInputChange}
                  required
                  fieldErrors={fieldErrors}
                />
                <InputField
                  label="Straße + Nr"
                  name="strasse"
                  type="text"
                  value={formData.strasse}
                  onChange={handleInputChange}
                  required
                  className="md:col-span-2"
                  fieldErrors={fieldErrors}
                />
              </div>
              <div className="mt-4">
                <button
                  type="button"
                  onClick={nextStep}
                  className="rounded bg-[#ff4500] px-6 py-2 text-white hover:bg-[#ff6a33]"
                >
                  Weiter
                </button>
              </div>
            </StepContainer>
          )
        case 2:
          return (
            <StepContainer title="Ihre Kontaktdaten">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <InputField
                  label="Vorname"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  fieldErrors={fieldErrors}
                />
                <InputField
                  label="Nachname"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  fieldErrors={fieldErrors}
                />
                <InputField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  fieldErrors={fieldErrors}
                />
                <InputField
                  label="Telefon"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  fieldErrors={fieldErrors}
                />
              </div>
              <div className="mt-8 flex justify-end">
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="rounded bg-[#ff4500] px-8 py-4 text-lg text-white hover:bg-[#ff6a33]"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Wird gesendet...' : 'Absenden'}
                </button>
              </div>
            </StepContainer>
          )
        case 3:
          // Calendar Widget based on Bundesland selection
          const northernStates = [
            'Hamburg',
            'Bremen',
            'Berlin',
            'Niedersachsen',
            'Mecklenburg-Vorpommern',
            'Nordrhein-Westfalen',
            'Hessen',
            'Schleswig-Holstein',
          ]

          const southernStates = [
            'Bayern',
            'Sachsen',
            'Baden-Württemberg',
            'Rheinland-Pfalz',
            'Saarland',
            'Thüringen',
            'Brandenburg',
            'Sachsen-Anhalt',
          ]

          return (
            <StepContainer title="Das hat geklappt!">
              <div className="text-center">
                <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-100">
                  <svg
                    className="h-12 w-12 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                </div>
                <p className="mb-6 text-gray-600">
                  In der Zwischenzeit können Sie sich gerne auf unserer Wissensplattform umschauen
                  oder entspannt unsere informativen YouTube-Videos anklicken.
                </p>

                {/* First row of buttons */}
                <div className="flex flex-col items-center space-y-4 sm:flex-row sm:justify-center sm:space-y-0 sm:space-x-4">
                  <a
                    href="https://www.youtube.com/@GoDeutschland.energiel%C3%B6sungen"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-lg bg-red-600 px-6 py-2 text-white hover:scale-103 hover:bg-red-700"
                  >
                    YouTube Kanal
                  </a>
                  <a
                    href="https://www.goenergy.solar/wiki"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-lg bg-blue-600 px-6 py-2 text-white hover:scale-103 hover:bg-blue-700"
                  >
                    Wissensplattform
                  </a>
                </div>

                {/* Separate button below */}
                <div className="mt-6">
                  <button
                    onClick={() => {
                      if (typeof window !== 'undefined') {
                        window.location.reload()
                      }
                    }}
                    className="cursor-pointer rounded-lg bg-[#ff4500] px-6 py-2 text-white hover:scale-103 hover:bg-[#e03e00]"
                  >
                    Neues Formular ausfüllen
                  </button>
                </div>

                {/* Calendar appointment section */}
                <div className="mt-8">
                  <h3 className="mb-4 text-xl font-semibold text-gray-800">
                    Vereinbaren Sie hier einen Termin
                  </h3>
                  <p className="mb-4 text-sm text-gray-600">
                    Bei einer Terminbuchung müssen Name und E-Mail Adresse erneut eingegeben werden!
                  </p>

                  {/* Calendar Widget based on Bundesland selection */}
                  {northernStates.includes(formData.bundesland) && (
                    <iframe
                      src="https://app.goenergy.solar/widget/booking/oVoG8FcnfBVlJZ5b2xXH"
                      style={{ width: '100%', border: 'none', overflow: 'hidden', height: '900px' }}
                      scrolling="yes"
                      id="oVoG8FcnfBVlJZ5b2xXH_1745579471589"
                    />
                  )}

                  {southernStates.includes(formData.bundesland) && (
                    <iframe
                      src="https://app.goenergy.solar/widget/booking/EDMGAoZ74MImFnJWQU0Y"
                      style={{ width: '100%', border: 'none', overflow: 'hidden', height: '900px' }}
                      scrolling="yes"
                      id="EDMGAoZ74MImFnJWQU0Y_1745578763338"
                    />
                  )}
                </div>
              </div>
            </StepContainer>
          )
        default:
          return null
      }
    }

    return null
  }

  const hasSkippedCalendar = React.useRef(false)
  useEffect(() => {
    if (
      formData.currentPath === 'common' &&
      formData.currentServiceIndex === 1 &&
      !hasSkippedCalendar.current
    ) {
      hasSkippedCalendar.current = true
      nextStep()
    }
    // Only run when these values change
  }, [formData.currentPath, formData.currentServiceIndex])

  return (
    <div className={`mx-auto flex h-full max-w-6xl flex-col px-6 py-6 ${className}`}>
      {/* Header */}
      <div className="mb-8 text-center">
        <h1 className="text-4xl font-bold text-white">Energieberatung</h1>
        <p className="mt-3 text-lg text-white/80">
          Beantworten Sie einige Fragen, um Ihre individuelle Energielösung zu finden
        </p>
      </div>

      {/* Progress bar */}
      <div className="mb-8">
        <div className="h-3 w-full rounded-full bg-gray-200">
          <div
            className="h-3 rounded-full bg-[#ff4500] transition-all duration-500"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <div className="mt-3 text-right text-sm text-white">
          Schritt {formData.currentStep} von {calculateTotalSteps()}
        </div>
      </div>

      {/* Form content */}
      <form onSubmit={(e) => e.preventDefault()} className="flex flex-grow flex-col">
        <AnimatePresence mode="wait">
          <motion.div
            key={formData.currentStep}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="flex flex-grow flex-col"
          >
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>

        {/* Navigation buttons */}
        {!submitted && formData.currentStep > 1 && (
          <div className="mt-auto flex justify-between pt-4">
            <button
              type="button"
              onClick={prevStep}
              className="rounded border border-gray-300 bg-white px-6 py-3 text-lg text-gray-700 hover:bg-gray-50"
            >
              Zurück
            </button>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="mt-4 rounded-md bg-red-50 p-4 text-red-800">
            <p>{error}</p>
          </div>
        )}
      </form>
    </div>
  )
}

// Helper Components
const StepContainer = ({ title, children }: { title: string; children: React.ReactNode }) => {
  return (
    <div className="flex flex-grow flex-col rounded-lg bg-white p-6 shadow-md">
      <h2 className="mb-6 text-center text-xl font-semibold">{title}</h2>
      <div className="flex flex-grow flex-col">{children}</div>
    </div>
  )
}

const OptionGrid = ({ children }: { children: React.ReactNode }) => {
  return <div className="mb-auto grid grid-cols-1 gap-6 md:grid-cols-2">{children}</div>
}

// Multi-select option card for first step
const MultiSelectCard = ({
  title,
  description,
  icon,
  onClick,
  selected,
}: {
  title: string
  description?: string
  icon?: string
  onClick: () => void
  selected: boolean
}) => {
  return (
    <div
      onClick={onClick}
      className={`cursor-pointer rounded-lg border p-6 text-center transition-all duration-200 hover:shadow-md ${
        selected ? 'border-[#ff4500] bg-orange-50' : 'border-gray-200'
      } flex h-full flex-col justify-between`}
    >
      {icon && (
        <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gray-100">
          <Image src={icon} alt={title} width={45} height={45} />
        </div>
      )}
      <div>
        <h3 className="text-lg font-semibold">{title}</h3>
        {description && <p className="mt-2 text-sm text-gray-600">{description}</p>}
      </div>
      <div className="mt-4 flex justify-center">
        <div
          className={`h-7 w-7 rounded-full border-2 ${
            selected ? 'border-[#ff4500] bg-[#ff4500]' : 'border-gray-300'
          } flex items-center justify-center`}
        >
          {selected && (
            <svg
              className="h-5 w-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          )}
        </div>
      </div>
    </div>
  )
}

const OptionCard = ({
  title,
  description,
  icon,
  onClick,
  selected,
  className,
}: {
  title: string
  description?: string
  icon?: string
  onClick: () => void
  selected: boolean
  className?: string
}) => {
  return (
    <div
      onClick={onClick}
      className={`cursor-pointer rounded-lg border p-3 text-center transition-all duration-200 hover:shadow-md ${
        selected ? 'border-[#ff4500] bg-orange-50' : 'border-gray-200'
      } ${className || ''}`}
    >
      {icon && (
        <div className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
          <Image src={icon} alt={title} width={40} height={40} />
        </div>
      )}
      <h3 className="font-semibold">{title}</h3>
      {description && <p className="mt-1 text-sm text-gray-600">{description}</p>}
    </div>
  )
}

const InputField = ({
  label,
  name,
  type,
  value,
  onChange,
  placeholder,
  required = false,
  className,
  fieldErrors = {},
}: {
  label: string
  name: string
  type: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  required?: boolean
  className?: string
  fieldErrors?: { [key: string]: string }
}) => {
  // Get error message if it exists
  const errorMessage = fieldErrors[name]

  return (
    <div className={`group relative mb-6 ${className || ''}`}>
      <input
        type={type}
        name={name}
        id={name}
        value={value}
        onChange={onChange}
        placeholder=" "
        required={required}
        className={`peer w-full appearance-none rounded-md border ${errorMessage ? 'border-red-500' : 'border-gray-300'} bg-transparent px-4 py-3 text-base text-gray-900 focus:border-[#ff4500] focus:ring-0 focus:outline-none`}
      />
      <label
        htmlFor={name}
        className={`pointer-events-none absolute top-3 left-4 origin-[0] transform text-base ${errorMessage ? 'text-red-500' : 'text-gray-500'} transition-all duration-200 ease-in-out peer-focus:-top-4 peer-focus:left-4 peer-focus:bg-transparent peer-focus:px-1 peer-focus:text-sm peer-focus:text-[#ff4500] peer-[&:not(:placeholder-shown)]:-top-4 peer-[&:not(:placeholder-shown)]:left-4 peer-[&:not(:placeholder-shown)]:bg-transparent peer-[&:not(:placeholder-shown)]:px-1 peer-[&:not(:placeholder-shown)]:text-sm`}
      >
        {label}
        {required ? ' *' : ''}
      </label>
      {errorMessage && <p className="mt-2 text-sm text-red-500">{errorMessage}</p>}
    </div>
  )
}

export default MultiStepForm
